import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import { CalendarCheck, Loader2, Phone, Video } from "lucide-react";
import { toast } from "sonner";
import { Overline, Reveal } from "./Primitives";
import { useLang, useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (d, fb) => (typeof d === "string" ? d : fb);

const dpClass = {
  months: "flex flex-col", month: "space-y-4", caption: "flex justify-center relative items-center h-10",
  caption_label: "font-display text-2xl capitalize", nav: "flex items-center",
  nav_button: "h-9 w-9 grid place-items-center rounded-full border border-[var(--line)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors",
  nav_button_previous: "absolute left-0", nav_button_next: "absolute right-0",
  table: "w-full border-collapse", head_row: "flex", head_cell: "flex-1 font-mono text-[11px] uppercase text-stone-400 py-2",
  row: "flex w-full mt-1", cell: "flex-1 aspect-square p-0.5",
  day: "w-full h-full rounded-full text-sm transition-colors hover:bg-white/10 disabled:hover:bg-transparent",
  day_selected: "!bg-[var(--gold)] !text-[#0c0a09] font-semibold", day_today: "border border-[var(--gold)]/50",
  day_disabled: "text-stone-600 cursor-not-allowed", day_outside: "opacity-30",
};

const Slots = ({ date, slot, setSlot, t }) => {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!date) return undefined;
    let alive = true;
    setSlots(null);
    axios.get(`${API}/bookings/slots`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => { if (alive) setSlots(Array.isArray(r.data?.slots) ? r.data.slots : []); })
      .catch(() => { if (alive) setSlots([]); });
    return () => { alive = false; };
  }, [date]);

  if (!date) return <p className="text-stone-400 text-sm">{t.pickDay}</p>;
  if (slots === null) return <Loader2 className="animate-spin text-[var(--gold)]" />;
  if (!slots.length) return <p className="text-stone-400 text-sm" data-testid="booking-no-slots">{t.noSlots}</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
      {slots.map((s) => (
        <button key={s} type="button" onClick={() => setSlot(s)} data-testid={`booking-slot-${s.replace(":", "")}`}
          className={`py-2.5 rounded-full text-sm font-mono border transition-colors ${slot === s ? "bg-[var(--gold)] text-[#0c0a09] border-[var(--gold)]" : "border-[var(--line)] text-stone-200 hover:border-[var(--gold)]"}`}>
          {s}
        </button>
      ))}
    </div>
  );
};

const isBlocked = (d) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const max = new Date(today); max.setDate(max.getDate() + 60);
  return d < today || d > max || d.getDay() === 0 || d.getDay() === 6;
};

export const BookingCalendar = () => {
  const t = useT().booking;
  const { lang } = useLang();
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [mode, setMode] = useState("video");
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pick = (d) => { setDate(d); setSlot(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) { toast.error(t.needSlot); return; }
    if (form.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(form.email) || (mode === "phone" && form.phone.trim().length < 6)) { toast.error(t.invalid); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, mode, lang, date: format(date, "yyyy-MM-dd"), time: slot });
      if (typeof window.gtag === "function") window.gtag("event", "book_call", { mode });
      setDone({ date, slot });
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail, t.err));
      if (err.response?.status === 409) { setSlot(""); setDate(new Date(date)); }
    } finally { setLoading(false); }
  };

  const loc = lang === "it" ? itLocale : enUS;

  return (
    <section id="prenota" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Overline>{t.over}</Overline>
          <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">{t.title}</h2>
          <p className="mt-6 max-w-2xl text-stone-300 text-base md:text-lg">{t.sub}</p>
        </Reveal>
        {done ? (
          <Reveal>
            <div className="mt-14 max-w-2xl rounded-[28px] border border-[var(--gold)]/50 bg-gradient-to-b from-[#2a2219] to-[#15120f] p-8 md:p-10" data-testid="booking-success">
              <CalendarCheck className="text-[var(--gold)]" size={30} />
              <p className="mt-5 font-display text-4xl">{t.okTitle}</p>
              <p className="mt-3 font-display text-2xl text-[var(--gold)] capitalize">{format(done.date, "EEEE d MMMM", { locale: loc })} · {done.slot}</p>
              <p className="mt-4 text-stone-300">{t.ok}</p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="mt-14 grid lg:grid-cols-12 gap-6" data-testid="booking-form">
              <div className="lg:col-span-5 rounded-[28px] border border-[var(--line)] bg-[var(--card)] p-5 md:p-7" data-testid="booking-calendar">
                <DayPicker mode="single" selected={date} onSelect={pick} disabled={isBlocked} locale={loc} weekStartsOn={1}
                  fromDate={new Date()} classNames={dpClass} showOutsideDays={false} />
              </div>
              <div className="lg:col-span-7 rounded-[28px] border border-[var(--line)] bg-[var(--card)] p-6 md:p-8 space-y-7">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-3">
                    {date ? format(date, "EEEE d MMMM", { locale: loc }) : t.time} · 30 min
                  </p>
                  <Slots date={date} slot={slot} setSlot={setSlot} t={t} />
                </div>
                <div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{t.how}</span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[["video", Video], ["phone", Phone]].map(([k, Icon]) => (
                      <button type="button" key={k} onClick={() => setMode(k)} data-testid={`booking-mode-${k}`}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${mode === k ? "bg-[var(--gold)] text-[#0c0a09] border-[var(--gold)]" : "border-[var(--line)] text-stone-300 hover:border-stone-400"}`}>
                        <Icon size={15} /> {t.modes[k]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  {[["name", "text"], ["email", "email"], ["phone", "tel"]].map(([k, type]) => (
                    <label key={k} className={`block group ${k === "phone" && mode !== "phone" ? "hidden" : ""}`}>
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 group-focus-within:text-[var(--gold)]">{t[k]}</span>
                      <input type={type} data-testid={`booking-${k}-input`} className="field" value={form[k]} onChange={set(k)} maxLength={k === "phone" ? 30 : 80} />
                    </label>
                  ))}
                  <label className="block group sm:col-span-2">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 group-focus-within:text-[var(--gold)]">{t.note}</span>
                    <input data-testid="booking-note-input" className="field" value={form.note} onChange={set("note")} maxLength={500} />
                  </label>
                </div>
                <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="btn-gold w-full justify-center disabled:opacity-60">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <CalendarCheck size={18} />} {t.send}
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
